import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Minus, Plus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../lib/images";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

const EXPERIENCE_META = {
  boho: { name: "The Boho Picnic", img: IMAGES.bohoHero, desc: "experiences.bohoDesc" },
  floating: { name: "Floating Picnic", img: IMAGES.floatingHero, desc: "experiences.floatingDesc" },
  electronic: { name: "Picnic Electronic", img: IMAGES.electronicHero, desc: "experiences.electronicDesc" },
};

export default function Booking() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [experience, setExperience] = useState(params.get("experience") || "");
  const [pkg, setPkg] = useState(params.get("package") || "");
  const [eventId, setEventId] = useState(params.get("event") || "");
  const [guests, setGuests] = useState(2);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [availability, setAvailability] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [events, setEvents] = useState([]);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", requests: "" });
  const [submitting, setSubmitting] = useState(false);

  const maxGuests = experience === "floating" ? 2 : 30;
  const selectedEvent = events.find((e) => e.id === eventId);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!experience || experience === "electronic" || !date) return;
    setLoadingSlots(true);
    setSlot("");
    api.get(`/availability?experience=${experience}&date=${date}`)
      .then((r) => setAvailability(r.data.slots))
      .catch(() => { setAvailability([]); toast.error(t("booking.noSlots")); })
      .finally(() => setLoadingSlots(false));
  }, [experience, date, t]);

  const steps = useMemo(() => {
    const s = ["experience"];
    if (experience === "boho") s.push("package");
    if (experience === "electronic") s.push("event");
    s.push("guests");
    if (experience !== "electronic") s.push("datetime");
    s.push("details", "payment");
    return s;
  }, [experience]);

  const [stepIdx, setStepIdx] = useState(0);
  const step = steps[stepIdx];

  const validStep = (s) => {
    if (s === "experience") return !!experience;
    if (s === "package") return !!pkg;
    if (s === "event") return !!eventId;
    if (s === "guests") return guests >= 1 && guests <= maxGuests;
    if (s === "datetime") return !!date && !!slot;
    if (s === "details") return customer.name.trim() && /.+@.+\..+/.test(customer.email) && customer.phone.trim();
    return true;
  };

  // Deep-links (e.g. ?experience=boho&package=premium) skip pre-answered steps
  useEffect(() => {
    const firstIncomplete = steps.findIndex((s) => !validStep(s));
    if (firstIncomplete > 0) setStepIdx(firstIncomplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unitPrice = experience === "boho" ? (pkg === "premium" ? 79 : 59)
    : experience === "floating" ? 89
    : selectedEvent ? selectedEvent.price : 0;
  const total = unitPrice * guests;

  const canContinue = () => validStep(step);

  const next = () => { if (canContinue() && stepIdx < steps.length - 1) setStepIdx(stepIdx + 1); };
  const back = () => { if (stepIdx > 0) setStepIdx(stepIdx - 1); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        experience, package: pkg || null, event_id: eventId || null,
        date: date || null, time_slot: slot || null, guests,
        customer: { name: customer.name, email: customer.email, phone: customer.phone },
        requests: customer.requests, lang,
      });
      const { data: payment } = await api.post("/payments/checkout", {
        booking_id: booking.id, origin_url: window.location.origin,
      });
      if (payment.mode === "stripe" && payment.checkout_url) {
        window.location.href = payment.checkout_url;
        return;
      }
      navigate(`/booking/confirmation/${booking.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("booking.bookingError"));
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels = {
    experience: t("booking.step1"), package: t("booking.step2"), event: t("booking.step1"),
    guests: t("booking.step3"), datetime: t("booking.step4"), details: t("booking.step5"), payment: t("booking.step6"),
  };

  return (
    <div data-testid="booking-page" className="pt-36 pb-24 sm:pb-32">
      <SEO title="Booking — The Nice Picnic, Côte d'Azur"
        description="Book your luxury picnic experience on the French Riviera: The Boho Picnic, Floating Picnic or Picnic Electronic." />
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="overline-label">{t("booking.overline")}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-4">{t("booking.title")}</h1>
          <div className="w-16 h-px bg-gold mt-6 mx-auto" />
        </Reveal>

        {/* Progress */}
        <div className="mt-12 flex items-center justify-center gap-0 flex-wrap" data-testid="booking-progress">
          {steps.map((s, i) => (
            <div key={`${s}-${i}`} className="flex items-center">
              <button onClick={() => i < stepIdx && setStepIdx(i)} data-testid={`progress-step-${s}`}
                className={`flex items-center gap-2 text-[0.65rem] sm:text-xs uppercase tracking-[0.15em] ${i === stepIdx ? "text-terracotta" : i < stepIdx ? "text-olive" : "text-earth/40"}`}>
                <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-[0.6rem] ${i === stepIdx ? "border-terracotta" : i < stepIdx ? "border-olive" : "border-champagne"}`}>{i + 1}</span>
                <span className="hidden sm:inline">{stepLabels[s]}</span>
              </button>
              {i < steps.length - 1 && <span className="w-6 sm:w-10 h-px bg-champagne mx-2" />}
            </div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            {step === "experience" && (
              <div data-testid="step-experience" className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {Object.entries(EXPERIENCE_META).map(([key, meta]) => (
                  <button key={key} data-testid={`select-experience-${key}`} onClick={() => { setExperience(key); setPkg(key === "boho" ? pkg : ""); setSlot(""); }}
                    className={`text-left border rounded-3xl overflow-hidden transition-all duration-300 ${experience === key ? "border-terracotta shadow-[0_12px_40px_rgba(200,109,81,0.15)]" : "border-champagne hover:border-gold"}`}>
                    <img src={meta.img} alt={meta.name} className="w-full h-32 object-cover" />
                    <div className="p-4">
                      <h3 className="font-serif text-lg text-ink">{meta.name}</h3>
                      <p className="text-xs text-earth mt-1 line-clamp-2">{t(meta.desc)}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {step === "package" && (
              <div data-testid="step-package" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {["medium", "premium"].map((p) => (
                  <button key={p} data-testid={`select-package-${p}`} onClick={() => setPkg(p)}
                    className={`text-left border rounded-3xl p-7 transition-all duration-300 ${pkg === p ? "border-terracotta bg-cream" : "border-champagne hover:border-gold"}`}>
                    <h3 className="font-serif text-2xl text-ink">{t(`boho.${p}`)}</h3>
                    <p className="font-serif text-3xl text-clay mt-2">{p === "premium" ? 79 : 59} € <span className="text-sm text-earth">{t("booking.perPerson")}</span></p>
                  </button>
                ))}
              </div>
            )}

            {step === "event" && (
              <div data-testid="step-event" className="space-y-4">
                <h3 className="font-serif text-2xl text-ink mb-4">{t("booking.chooseEvent")}</h3>
                {events.length === 0 && <p className="text-earth">{t("electronic.empty")}</p>}
                {events.map((ev) => (
                  <button key={ev.id} data-testid={`select-event-${ev.id}`} onClick={() => setEventId(ev.id)} disabled={ev.places_left <= 0 || !ev.booking_open}
                    className={`w-full text-left border rounded-2xl p-6 transition-all duration-300 disabled:opacity-50 ${eventId === ev.id ? "border-terracotta bg-cream" : "border-champagne hover:border-gold"}`}>
                    <div className="flex flex-wrap justify-between gap-3">
                      <div>
                        <h4 className="font-serif text-xl text-ink">{ev.title}</h4>
                        <p className="text-sm text-earth mt-1">{new Date(`${ev.date}T12:00:00`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { weekday: "long", day: "numeric", month: "long" })} · {ev.start_time} · {ev.location}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-serif text-2xl text-clay">{ev.price} €</p>
                        <p className="text-xs text-earth">{ev.places_left} {t("booking.placesLeft")}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {step === "guests" && (
              <div data-testid="step-guests" className="text-center border border-champagne rounded-3xl p-10">
                <h3 className="font-serif text-2xl text-ink">{t("booking.guests")}</h3>
                <div className="flex items-center justify-center gap-8 mt-8">
                  <button data-testid="guests-minus" onClick={() => setGuests(Math.max(1, guests - 1))}
                    className="w-12 h-12 rounded-full border border-champagne flex items-center justify-center text-ink hover:border-terracotta hover:text-terracotta transition-colors">
                    <Minus size={18} />
                  </button>
                  <span data-testid="guests-count" className="font-serif text-6xl text-ink w-20">{guests}</span>
                  <button data-testid="guests-plus" onClick={() => setGuests(Math.min(maxGuests, guests + 1))}
                    className="w-12 h-12 rounded-full border border-champagne flex items-center justify-center text-ink hover:border-terracotta hover:text-terracotta transition-colors">
                    <Plus size={18} />
                  </button>
                </div>
                <p className="text-xs text-earth/70 mt-6">{t("booking.maxGuests").replace("{max}", maxGuests)}</p>
              </div>
            )}

            {step === "datetime" && (
              <div data-testid="step-datetime" className="space-y-8">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-earth mb-3">{t("booking.chooseDate")}</label>
                  <input data-testid="booking-date" type="date" min={new Date().toISOString().split("T")[0]}
                    className="input-elegant max-w-xs" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                {date && (
                  <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-earth mb-3">{t("booking.chooseTime")}</label>
                    {loadingSlots ? (
                      <p className="text-sm text-earth">{t("booking.loadingSlots")}</p>
                    ) : availability && availability.length > 0 ? (
                      <div className="flex flex-wrap gap-3" data-testid="slot-list">
                        {availability.map((s) => (
                          <button key={s.time} data-testid={`slot-${s.time}`} disabled={!s.available} onClick={() => setSlot(s.time)}
                            className={`px-6 py-3.5 rounded-full border text-sm tracking-wide transition-all duration-300 ${!s.available ? "border-champagne/60 text-earth/40 line-through cursor-not-allowed" : slot === s.time ? "border-terracotta bg-terracotta text-ivory" : "border-champagne text-ink hover:border-terracotta hover:text-terracotta"}`}>
                            {s.time.replace("-", " – ")}
                          </button>
                        ))}
                      </div>
                    ) : <p className="text-sm text-earth">{t("booking.noSlots")}</p>}
                  </div>
                )}
              </div>
            )}

            {step === "details" && (
              <div data-testid="step-details" className="space-y-5">
                <h3 className="font-serif text-2xl text-ink">{t("booking.yourDetails")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input data-testid="booking-name" className="input-elegant" placeholder={`${t("booking.name")} *`}
                    value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
                  <input data-testid="booking-email" type="email" className="input-elegant" placeholder={`${t("booking.email")} *`}
                    value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
                </div>
                <input data-testid="booking-phone" type="tel" className="input-elegant" placeholder={`${t("booking.phone")} *`}
                  value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
                <textarea data-testid="booking-requests" rows={4} className="input-elegant" placeholder={t("booking.requestsPlaceholder")}
                  value={customer.requests} onChange={(e) => setCustomer({ ...customer, requests: e.target.value })} />
              </div>
            )}

            {step === "payment" && (
              <div data-testid="step-payment" className="space-y-6">
                <div className="border border-champagne rounded-3xl p-7 bg-cream/50">
                  <div className="flex items-center gap-3 text-olive">
                    <ShieldCheck size={20} />
                    <p className="text-sm font-medium text-ink">Stripe · Visa · Mastercard · Apple Pay · Google Pay</p>
                  </div>
                  <p className="text-sm text-earth font-light mt-4 leading-relaxed">{t("booking.paymentNote")}</p>
                  <p className="text-sm text-clay font-light mt-3 leading-relaxed border-t border-champagne pt-4">{t("booking.stripePending")}</p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-12">
              <button data-testid="booking-back-btn" onClick={back} disabled={stepIdx === 0}
                className="btn-outline disabled:opacity-30 !px-6 !py-3">
                <ArrowLeft size={15} /> {t("booking.back")}
              </button>
              {step === "payment" ? (
                <button data-testid="booking-submit-btn" onClick={submit} disabled={submitting}
                  className="btn-primary disabled:opacity-60">
                  {t("booking.confirmReservation")} <ArrowRight size={15} />
                </button>
              ) : (
                <button data-testid="booking-next-btn" onClick={next} disabled={!canContinue()}
                  className="btn-primary disabled:opacity-40">
                  {t("booking.continue")} <ArrowRight size={15} />
                </button>
              )}
            </div>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-4">
            <div data-testid="booking-summary" className="sticky top-28 border border-champagne rounded-3xl p-7 bg-cream/50">
              <h3 className="font-serif text-2xl text-ink">{t("booking.summary")}</h3>
              <div className="mt-5 space-y-3 text-sm text-earth">
                <div className="flex justify-between"><span>{t("booking.step1")}</span>
                  <span className="text-ink font-medium">{experience ? EXPERIENCE_META[experience].name : "—"}</span></div>
                {experience === "boho" && pkg && (
                  <div className="flex justify-between"><span>{t("booking.step2")}</span><span className="text-ink">{t(`boho.${pkg}`)}</span></div>)}
                {selectedEvent && (
                  <div className="flex justify-between"><span>Event</span><span className="text-ink text-right">{selectedEvent.title}</span></div>)}
                {date && <div className="flex justify-between"><span>Date</span><span className="text-ink">{date}</span></div>}
                {slot && <div className="flex justify-between"><span>{t("booking.step4")}</span><span className="text-ink">{slot.replace("-", " – ")}</span></div>}
                <div className="flex justify-between"><span>{t("booking.guests")}</span><span className="text-ink">{guests}</span></div>
              </div>
              <div className="border-t border-champagne mt-5 pt-5 flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-[0.2em] text-earth">{t("booking.total")}</span>
                <span data-testid="summary-total" className="font-serif text-3xl text-clay">{total.toFixed(0)} €</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
